import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import imageResizer from 'js/image-resizer-utils';
import ResponsiveImageSource from 'components/Models/ResponsiveImageSource';
import './ResponsiveImage.scss';

export const transforms = {
  fill: 'fill',
  fit: 'fit',
};

const retinaMultipliers = [1, 2];

const buildResizedSet = (src, width, height, transform, focusPoint) => {
  if (!width && !height) return [];

  const resizeType =
    transform === transforms.fill
      ? imageResizer.Transforms.downFill
      : imageResizer.Transforms.downFit;
  const x = focusPoint?.x ?? 0;
  const y = focusPoint?.y ?? 0;

  return retinaMultipliers.map(multiplier => {
    const resizeWidth = Math.round(width * multiplier);
    const resizeHeight = Math.round(height * multiplier);

    return {
      url: imageResizer.buildResizeUrl(
        src,
        resizeWidth,
        resizeHeight,
        resizeType,
        x,
        y
      ),
      descriptor: `${multiplier}x`,
    };
  });
};

const ResponsiveImage = ({
  src,
  alt = '',
  className,
  sources = [],
  fluid = false,
  fetchPriority = 'auto',
  loading = 'eager',
  transform = transforms.fill,
  focusPoint,
}) => {
  if (!src) return null;

  return (
    <picture
      className={cn('responsive-image', className, {
        'responsive-image_fluid': fluid,
      })}
    >
      {src && imageResizer.checkImageCanBeResized(src)
        ? sources
            .sort((a, b) => a.media - b.media)
            .map(({ width, height, media }, index) => {
              const resizedSet = buildResizedSet(
                src,
                width,
                height,
                transform,
                focusPoint
              );
              const srcSet = resizedSet
                .map(({ url, descriptor }) => `${url} ${descriptor}`)
                .join(', ');
              return (
                <source
                  key={media || width}
                  {...(media && {
                    media:
                      index !== sources.length - 1
                        ? `(max-width: ${media}px)`
                        : `(min-width: ${sources[index - 1].media}px)`,
                  })}
                  srcSet={srcSet}
                />
              );
            })
        : null}
      <img
        loading={loading}
        fetchPriority={fetchPriority}
        className="responsive-image__img"
        src={src}
        alt={alt}
      />
    </picture>
  );
};

ResponsiveImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  fluid: PropTypes.bool,
  sources: PropTypes.arrayOf(ResponsiveImageSource.propTypes),
  focusPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  transform: PropTypes.oneOf(Object.values(transforms)),
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  loading: PropTypes.oneOf(['eager', 'lazy']),
};

export default ResponsiveImage;
